import { Component, LOCALE_ID, inject } from '@angular/core';
import { formatDate } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { COMMON_IMPORTS } from '../../../../Shared/common-imports';
import { AvContentContainerComponent } from '../../../../Shared/Components/av-content-container/av-content-container.component';
import { AvDialogComponent } from '../../../../Shared/Components/av-dialog/av-dialog.component';
import { MsgService } from '../../../../Shared/Services/msg.service';
import { BillingFacade } from '../../../../../Facades/billing.facade';
import {
    BillingPlanOption,
    BillingProrationPreview,
} from '../../../../../Interfaces/api/billing.interface';
import { BillingSubscriptionCardComponent } from './Partials/subscription-card/subscription-card.component';
import { BillingEstimateCardComponent } from './Partials/estimate-card/estimate-card.component';
import { BillingPaymentMethodCardComponent } from './Partials/payment-method-card/payment-method-card.component';
import { BillingInvoicesCardComponent } from './Partials/invoices-card/invoices-card.component';
import { PlanPickerDialogComponent } from './Dialogs/plan-picker/plan-picker-dialog.component';

@Component({
    selector: 'app-billing',
    standalone: true,
    imports: [
        COMMON_IMPORTS,
        AvContentContainerComponent,
        BillingSubscriptionCardComponent,
        BillingEstimateCardComponent,
        BillingPaymentMethodCardComponent,
        BillingInvoicesCardComponent,
    ],
    templateUrl: './billing.component.html',
    styleUrls: ['./billing-page.scss', './billing.scss'],
})
export class BillingComponent {
    readonly facade = inject(BillingFacade);
    private readonly _dialog = inject(MatDialog);
    private readonly _msgService = inject(MsgService);
    private readonly _translate = inject(TranslateService);
    private readonly _locale = inject(LOCALE_ID);

    // Plan change

    async openPlanPicker(): Promise<void> {
        const plan = await firstValueFrom(
            this._dialog
                .open(PlanPickerDialogComponent, {
                    data: { plans: this.facade.plans(), isReadOnly: this.facade.isReadOnly() },
                    panelClass: 'av-dialog',
                })
                .afterClosed(),
        );

        if (!plan) {
            return;
        }

        await this.confirmUpgrade(plan);
    }

    async confirmUpgrade(plan: BillingPlanOption): Promise<void> {
        if (!plan.is_eligible_upgrade) {
            return;
        }

        try {
            const preview = await firstValueFrom(this.facade.previewPlan(plan.id));
            const confirmed = await firstValueFrom(this.openUpgradeDialog(preview.data).afterClosed());

            if (!confirmed) {
                return;
            }

            await firstValueFrom(this.facade.updatePlan(plan.id));
            this.refreshAll();
            this._msgService.showSnackBar('app.billing.messages.plan_changed');
        } catch (error) {
            this.showFailure(error);
        }
    }

    // Cancellation

    async cancelSubscription(): Promise<void> {
        const confirmed = await firstValueFrom(
            this._msgService
                .showConfirm(
                    'app.billing.cancel.title',
                    this._translate.instant('app.billing.cancel.confirm_body', {
                        date: this.localDate(this.facade.subscription()?.current_period_end),
                    }),
                    '',
                    'error_outline',
                    'error',
                )
                .afterClosed(),
        );

        if (!confirmed) {
            return;
        }

        try {
            await firstValueFrom(this.facade.cancel());
            this.refreshAll();
            this._msgService.showSnackBar('app.billing.messages.canceled');
        } catch (error) {
            this.showFailure(error);
        }
    }

    async resumeSubscription(): Promise<void> {
        try {
            await firstValueFrom(this.facade.resume());
            this.refreshAll();
            this._msgService.showSnackBar('app.billing.messages.resumed');
        } catch (error) {
            this.showFailure(error);
        }
    }

    // Payment method

    async updateCard(): Promise<void> {
        try {
            const response = await firstValueFrom(this.facade.createPaymentMethodSession());
            const url = response?.data?.url;
            if (url) {
                window.location.href = url;
            }
        } catch (error) {
            this.showFailure(error);
        }
    }

    async retryPayment(): Promise<void> {
        try {
            await firstValueFrom(this.facade.retryPayment());
            this.refreshAll();
            this._msgService.showSnackBar('app.billing.messages.payment_retried');
        } catch (error) {
            this.showFailure(error);
        }
    }

    // Documents

    async changeContact(): Promise<void> {
        const email = await firstValueFrom(this.openEmailPrompt('app.billing.contact.title', 'app.billing.contact.hint', this.facade.billingContact()));

        if (!email) {
            return;
        }

        try {
            await firstValueFrom(this.facade.updateContact(email));
            this.facade.reloadOverview();
            this._msgService.showSnackBar('app.billing.contact.saved');
        } catch (error) {
            this.showFailure(error);
        }
    }

    // Shared

    onInvoicePage(pageIndex: number): void {
        this.facade.setInvoicePage(pageIndex);
    }

    retryEstimate(): void {
        this.facade.reloadEstimate();
    }

    private openEmailPrompt(titleKey: string, hintKey: string, value: string | null) {
        return this._msgService
            .showPrompt(this._translate.instant(titleKey), this._translate.instant(hintKey), value ?? '')
            .afterClosed();
    }

    private openUpgradeDialog(preview: BillingProrationPreview) {
        return this._dialog.open(AvDialogComponent, {
            data: {
                title: this._translate.instant('app.billing.upgrade.title'),
                type: 'confirm',
                value: true,
                entries: [
                    { labelKey: 'app.billing.plans.title', value: preview.plan_name },
                    { labelKey: 'app.billing.upgrade.charged_today', value: this.money(preview.amount_due_now, preview.currency) },
                    { labelKey: 'app.billing.upgrade.new_recurring_total', value: this.money(preview.next_invoice_total, preview.currency) },
                    { labelKey: 'app.billing.upgrade.card_charged', value: preview.payment_method?.last4 ?? '-' },
                ],
                subtext: this._translate.instant('app.billing.upgrade.access_warning'),
            },
            panelClass: 'av-dialog',
        });
    }

    private refreshAll(): void {
        this.facade.reloadOverview();
        this.facade.reloadEstimate();
        this.facade.reloadInvoices();
    }

    private localDate(value: string | null | undefined): string {
        return value ? formatDate(value, 'mediumDate', this._locale) : '';
    }

    private money(amount: number | null, currency: string): string {
        if (amount === null) {
            return '-';
        }
        return new Intl.NumberFormat(this._translate.currentLang, { style: 'currency', currency }).format(amount);
    }

    private showFailure(error: unknown): void {
        const body = (error as { error?: { message?: string; data?: { decline_message?: string } } })?.error;
        const reason = body?.data?.decline_message ?? body?.message ?? 'app.billing.errors.stripe_failed';
        this._msgService.showSnackBar(reason, { type: 'error' });
    }
}
